import { Selector, SocialNetworksAvailables, UserInDetails, defaultUser, onSocialNetworkConnection } from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import React, { RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './select-social-network.component.scoped.scss'
import './select-social-network.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  socialNetwork: string
  users: UserInDetails[]
  userSelected: UserInDetails
  footerCase: string
  loading: boolean
  onChange?: (value: any) => any
}

export default ({ socialNetwork = '', users = [], userSelected = null, footerCase = '', loading = false, onChange }: Props) => {
  const divRef: RefObject<HTMLDivElement> = React.createRef()
  const ulRef: RefObject<HTMLUListElement> = React.createRef()
  const [t] = useTranslation('selectSocialNetwork')
  const [state, setState] = useState({
    user: {} as UserInDetails,
  })

  const socialNetworks = useMemo(
    () => [
      {
        key: 'instagram',
        icon: 'fa-brands fa-instagram',
      },
      {
        key: 'youtube',
        icon: 'fa-brands fa-youtube',
      },
      {
        key: 'tiktok',
        icon: 'fa-brands fa-tiktok',
      },
      {
        key: 'twitter',
        icon: 'fa-brands fa-x-twitter',
      },
    ],
    []
  )

  useEffect(() => {
    if (!state?.user?.idNetwork && userSelected?.idNetwork) {
      setState({ ...state, user: userSelected })
    }
  }, [state, users, userSelected])

  const handleChange = useCallback(
    (user: UserInDetails): void => {
      state.user = user
      setState({ ...state })
      if (onChange && typeof onChange === 'function') {
        onChange(user)
      }
    },
    [state, onChange]
  )

  const isTouchDevice = () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0
  }

  useEffect(() => {
    const divGrab = divRef?.current
    const elements: any = document.getElementsByClassName('ul-item-grab')

    let pos = { top: 0, left: 0, x: 0, y: 0 }

    const mouseMoveHandler = function (e) {
      divGrab.scrollTop = 100
      divGrab.scrollLeft = 150

      const dx = e.clientX - pos.x
      const dy = e.clientY - pos.y

      divGrab.scrollTop = pos.top - dy
      divGrab.scrollLeft = pos.left - dx
    }

    const mouseUpHandler = function () {
      document.removeEventListener('mousemove', mouseMoveHandler)
      document.removeEventListener('mouseup', mouseUpHandler)

      if (divGrab?.offsetWidth < ulRef?.current?.offsetWidth) {
        if (elements) {
          for (const element of elements) {
            element.style.removeProperty('cursor')
            element.style.removeProperty('user-select')
          }
        }

        divGrab.style.removeProperty('cursor')
        divGrab.style.removeProperty('user-select')
      }
    }

    const mouseDownHandler = function (e) {
      if (divGrab && !isTouchDevice() && divGrab?.offsetWidth < ulRef?.current?.offsetWidth) {
        if (elements) {
          for (const element of elements) {
            element.style.cursor = 'grabbing'
            element.style.userSelect = 'none'
          }
        }

        divGrab.style.cursor = 'grabbing'
        divGrab.style.userSelect = 'none'

        pos = {
          left: divGrab.scrollLeft,
          top: divGrab.scrollTop,

          x: e.clientX,
          y: e.clientY,
        }

        document.addEventListener('mousemove', mouseMoveHandler)
        document.addEventListener('mouseup', mouseUpHandler)
      }
    }

    divGrab?.addEventListener('mousedown', mouseDownHandler)

    return () => {
      document.removeEventListener('mousemove', mouseMoveHandler)
      document.removeEventListener('mouseup', mouseUpHandler)
      divGrab?.removeEventListener('mousedown', mouseDownHandler)
    }
  }, [divRef, ulRef])

  const returnIconCase = useCallback(
    (user: UserInDetails) => {
      if (user?.idNetwork === userSelected?.idNetwork) {
        if (footerCase === 'recruited') {
          return <i className="change-bg fa-solid fa-circle-check" style={{ color: 'var(--green)', fontSize: '0.9rem' }}></i>
        }
        if (['customerReproved', 'profileFalse'].includes(footerCase)) {
          return <i className="change-bg fa-solid fa-circle-xmark" style={{ color: 'var(--red)', fontSize: '0.9rem' }}></i>
        }
        if (footerCase === 'renewToken') {
          return <i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--gold)', fontSize: '0.9rem' }}></i>
        }
      }
    },
    [userSelected, footerCase]
  )

  return (
    <div className="select-social-network-component">
      <div className={`icon-social-network background-${socialNetwork}`}>
        <i
          className={`${socialNetworks?.find((e) => e?.key === socialNetwork)?.icon}`}
          style={{ color: 'var(--white)', fontSize: '1.5rem' }}
        ></i>
      </div>
      <div className="grab" ref={divRef}>
        <ul ref={ulRef}>
          {users?.map((user) => (
            <li key={user?.idNetwork}>
              <Selector
                name={`@${user?.username}${state?.user?.idNetwork === user?.idNetwork ? '-checked' : ''}`}
                id={`@${user?.username}${state?.user?.idNetwork === user?.idNetwork ? '-checked' : ''}`}
                value={state.user}
                checked={state?.user?.idNetwork === user?.idNetwork}
                onChange={(checked: boolean) => {
                  if (checked) {
                    handleChange(user)
                  }
                }}
                disabled={loading}
                type={'radio'}
                hideInput
                errorSpan={false}
                rightLabel={
                  <div
                    className={`social-network ${state?.user?.idNetwork === user?.idNetwork ? 'active' : ''} ul-item-grab`}
                    style={{ transitionDuration: '100ms' }}
                  >
                    <div className="avatar">
                      {loading && state?.user?.idNetwork === user?.idNetwork ? (
                        <div className="display-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                          <SqLoader size="small" />
                        </div>
                      ) : (
                        <div>
                          <img
                            src={`https://storage.googleapis.com/squid-profile-pictures/${user?.idNetwork}.jpg`}
                            alt={user?.username}
                            title={user?.username}
                            className="img-fluid"
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null
                              currentTarget.src = defaultUser
                            }}
                            draggable="false"
                          />
                          <div className="icon-case">{returnIconCase(user)}</div>
                        </div>
                      )}
                    </div>
                    <p className="user-name mt-1">{`@${user?.username}`}</p>
                  </div>
                }
              />
            </li>
          ))}
          <li>
            <div
              className="add-more ul-item-grab"
              role="button"
              tabIndex={0}
              onClick={() => onSocialNetworkConnection.next({ socialNetwork: socialNetwork as SocialNetworksAvailables, connect: true })}
            >
              <div className="avatar">
                <img src={defaultUser} alt="add-more" title="add-more" className="img-fluid" draggable="false" />
              </div>
              <div style={{ width: 0, height: 0 }}>
                <div className="badge-plus">
                  <p>+</p>
                </div>
              </div>
              <p className="add-text mt-1">{t('toConnect')}</p>
            </div>
          </li>
        </ul>
      </div>
    </div>
  )
}
